(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const routeRoot = window.Shopify?.routes?.root || '/';
  const strings = window.SKEEZY_STRINGS || {};
  const store = {
    get(key) { try { return localStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { localStorage.setItem(key, value); } catch {} }
  };
  const drawer = qs('[data-cart-drawer]');
  if (drawer) {
    const panel = qs('.drawer-panel', drawer);
    const onCartPage = document.body.classList.contains('template-cart');
    let lastFocus;
    let background = [];
    let revision = 0;
    let pending = 0;
    let stale = true;
    let queue = Promise.resolve();
    const errorBox = qs('[data-drawer-error]', drawer);
    const isOpen = () => drawer.classList.contains('is-open');
    const setError = (message) => {
      if (!errorBox) return;
      errorBox.hidden = !message;
      errorBox.textContent = message || '';
    };
    const controls = () => qsa('[data-drawer-qty], [data-drawer-add], [name="checkout"]', drawer);
    const setBusy = () => {
      const busy = pending > 0;
      drawer.classList.toggle('is-busy', busy);
      panel?.setAttribute('aria-busy', String(busy));
      controls().forEach((control) => {
        if (busy) {
          if (!control.hasAttribute('data-before-busy')) control.dataset.beforeBusy = String(control.disabled);
          control.disabled = true;
        } else if (control.hasAttribute('data-before-busy')) {
          control.disabled = control.dataset.beforeBusy === 'true';
          delete control.dataset.beforeBusy;
        }
        if (stale && control.name === 'checkout') control.disabled = true;
        else if (!busy && control.name === 'checkout') control.disabled = false;
      });
    };
    const swap = (html) => {
      if (!html) return false;
      const next = qs('[data-drawer-content]', new DOMParser().parseFromString(html, 'text/html'));
      const current = qs('[data-drawer-content]', drawer);
      if (!next || !current) return false;
      stale = false;
      const active = document.activeElement;
      const focusedKey = active?.closest('[data-line-key]')?.dataset.lineKey;
      const focusedAction = active?.hasAttribute('data-drawer-qty') ? active.dataset.drawerAction : active?.hasAttribute('data-drawer-close') ? 'close' : null;
      current.innerHTML = next.innerHTML;
      setBusy();
      if (isOpen() && current.contains(active) === false && !active?.isConnected && focusedAction) {
        const line = qsa('[data-line-key]', drawer).find((element) => element.dataset.lineKey === focusedKey);
        const replacement = focusedAction === 'close' ? qs('[data-drawer-close]', panel) : line && qsa('[data-drawer-qty]', line).find((element) => element.dataset.drawerAction === focusedAction);
        (replacement && !replacement.disabled ? replacement : qs('[data-drawer-close]', panel) || panel)?.focus({ preventScroll: true });
      }
      return true;
    };
    const syncCount = async () => {
      try {
        const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; });
      } catch {}
    };
    const refresh = async ({ showError = false } = {}) => {
      const requestedRevision = ++revision;
      stale = true;
      setBusy();
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', 'cart-drawer');
        const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error();
        const sections = await response.json();
        if (requestedRevision !== revision) return true;
        if (!swap(sections['cart-drawer'])) throw new Error();
        setError('');
        return true;
      } catch {
        if (showError && requestedRevision === revision) setError(strings.cart_refresh_error || 'Your bag could not refresh. Open the full bag to check it before checkout.');
        return false;
      }
    };
    const open = (html) => {
      window.SkeezyOverlays?.closeAll({ restoreFocus: false });
      window.SkeezyOffer?.hide({ restoreFocus: false });
      if (html) { ++revision; swap(html); setError(''); } else refresh({ showError: true });
      if (!isOpen()) {
        lastFocus = document.activeElement;
        let ancestor = drawer;
        background = [];
        while (ancestor.parentElement && ancestor !== document.body) {
          [...ancestor.parentElement.children].filter((element) => element !== ancestor && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)).forEach((element) => {
            background.push({ element, inert: element.inert });
            element.inert = true;
          });
          ancestor = ancestor.parentElement;
        }
      }
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.inert = false;
      document.documentElement.classList.add('drawer-open');
      (qs('[data-drawer-close]', panel) || panel)?.focus({ preventScroll: true });
    };
    const close = () => {
      if (!isOpen()) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      drawer.inert = true;
      document.documentElement.classList.remove('drawer-open');
      background.forEach(({ element, inert }) => { element.inert = inert; });
      background = [];
      if (lastFocus?.isConnected && !lastFocus.disabled && !lastFocus.closest('[inert], [hidden]')) lastFocus.focus({ preventScroll: true });
      else qs('.cart-link')?.focus({ preventScroll: true });
    };
    drawer.inert = true;
    const request = (path, payload) => {
      pending += 1;
      setBusy();
      const work = queue.catch(() => {}).then(async () => {
        ++revision;
        setError('');
        const isForm = payload instanceof FormData;
        if (isForm) {
          payload.set('sections', 'cart-drawer');
          payload.set('sections_url', window.location.pathname);
        }
        const response = await fetch(`${routeRoot}${path}`, {
          method: 'POST',
          headers: { Accept: 'application/json', ...(isForm ? {} : { 'Content-Type': 'application/json' }) },
          body: isForm ? payload : JSON.stringify({ ...payload, sections: 'cart-drawer', sections_url: window.location.pathname })
        });
        const data = await response.json().catch(() => null);
        ++revision;
        if (!response.ok) {
          await refresh();
          throw new Error(data?.description || data?.message || strings.cart_change_error || 'That change could not be made.');
        }
        if (!swap(data?.sections?.['cart-drawer'])) await refresh({ showError: true });
        await syncCount();
        return data || {};
      });
      queue = work;
      return work.catch((error) => { if (error instanceof TypeError) stale = true; throw error; }).finally(() => { pending -= 1; setBusy(); });
    };
    window.SkeezyCart = { open, close, refresh, add: (body) => request('cart/add.js', body) };
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) { close(); return; }
      if (pending) return;
      const qty = event.target.closest('[data-drawer-qty]');
      const add = event.target.closest('[data-drawer-add]');
      let mutation;
      if (qty) {
        const line = qty.closest('[data-line-key]');
        const quantity = Number(qty.dataset.drawerQty);
        if (line && Number.isInteger(quantity) && quantity >= 0) mutation = request('cart/change.js', { id: line.dataset.lineKey, quantity });
      } else if (add) mutation = request('cart/add.js', { items: [{ id: Number(add.dataset.drawerAdd), quantity: Math.max(1, Number(add.dataset.drawerAddQuantity) || 1) }] });
      mutation?.catch(async (error) => {
        await syncCount();
        setError(error instanceof TypeError ? (strings.network_error || 'Connection interrupted. Your bag may have changed. Check your bag before trying again.') : error.message);
      });
    });
    if (!onCartPage) qsa('.cart-link').forEach((link) => link.addEventListener('click', (event) => {
      if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      open();
    }));
    document.addEventListener('keydown', (event) => {
      if (!isOpen()) return;
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = qsa('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]', panel).filter((element) => !element.closest('[hidden]')).sort((first, second) => first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first) { event.preventDefault(); panel.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    });
    document.addEventListener('focusin', (event) => { if (isOpen() && !panel.contains(event.target)) (qs('[data-drawer-close]', panel) || panel).focus({ preventScroll: true }); });
    addEventListener('pageshow', (event) => { if (event.persisted) { refresh({ showError: isOpen() }); syncCount(); } });
  }
  const saleEnds = Number(document.documentElement.dataset.saleEnds);
  if (saleEnds > 0) {
    const pad = (value) => String(value).padStart(2, '0');
    let timer;
    let bagTimer;
    const tick = () => {
      const left = saleEnds - Date.now();
      if (left <= 0) {
        document.documentElement.classList.add('sale-over');
        clearInterval(timer);
        clearInterval(bagTimer);
        return;
      }
      const minutes = Math.max(1, Math.ceil(left / 60000));
      const hours = Math.floor(minutes / 60);
      if (hours >= 48) return;
      qsa('[data-sale-countdown]').forEach((clock) => { clock.textContent = (strings.sale_countdown || 'Ends in [hours]h [minutes]m').replace('[hours]', hours).replace('[minutes]', pad(minutes % 60)); });
    };
    const tickBag = () => {
      if (document.hidden || !drawer?.classList.contains('is-open')) return;
      const total = Math.max(0, Math.ceil((saleEnds - Date.now()) / 1000));
      const days = Math.floor(total / 86400);
      qsa('[data-bag-clock]').forEach((clock) => { clock.textContent = `${days ? `${days}d ` : ''}${pad(Math.floor((total % 86400) / 3600))}h ${pad(Math.floor((total % 3600) / 60))}m ${pad(total % 60)}s`; });
    };
    tick();
    if (!document.documentElement.classList.contains('sale-over')) {
      timer = setInterval(tick, 20000);
      bagTimer = setInterval(tickBag, 1000);
    }
    document.addEventListener('visibilitychange', () => { if (!document.hidden) { tick(); tickBag(); } });
  }
  const offer = qs('[data-offer]');
  const tab = qs('[data-offer-open]');
  if (offer && tab) {
    const key = 'skeezy_offer';
    const state = store.get(key);
    const posted = location.hash === '#OfferForm' && !!qs('[data-offer-success], [data-offer-error]', offer);
    let offerFocus;
    const triggers = [tab, ...qsa('[data-offer-trigger]')];
    const show = ({ focus = false, trigger = null } = {}) => {
      if (document.documentElement.classList.contains('drawer-open') || document.documentElement.classList.contains('menu-open')) return;
      offer.hidden = false;
      tab.hidden = true;
      triggers.forEach((element) => element.setAttribute('aria-expanded', 'true'));
      if (focus) {
        offerFocus = trigger || document.activeElement;
        (qs('input[type="email"], [data-offer-success] a', offer) || qs('[data-offer-close]', offer))?.focus({ preventScroll: true });
      }
    };
    const hide = ({ restoreFocus = true } = {}) => {
      const wasFocused = offer.contains(document.activeElement);
      offer.hidden = true;
      tab.hidden = store.get(key) === 'joined';
      triggers.forEach((element) => element.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && wasFocused && offerFocus?.isConnected) offerFocus.focus({ preventScroll: true });
    };
    window.SkeezyOffer = { hide };
    triggers.forEach((trigger) => trigger.addEventListener('click', (event) => { event.preventDefault(); show({ focus: true, trigger }); }));
    qsa('[data-offer-close]', offer).forEach((button) => button.addEventListener('click', () => {
      if (store.get(key) !== 'joined') store.set(key, 'dismissed');
      hide();
    }));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !offer.hidden && !drawer?.classList.contains('is-open')) { event.preventDefault(); hide(); } });
    if (posted) {
      if (qs('[data-offer-success]', offer)) store.set(key, 'joined');
      show({ focus: true, trigger: tab });
    } else if (state === 'joined') tab.hidden = true;
    else if (state === 'dismissed' || document.body.classList.contains('template-product')) tab.hidden = false;
    else {
      const onScroll = () => {
        if (window.scrollY <= window.innerHeight * 2 || document.documentElement.classList.contains('drawer-open') || document.documentElement.classList.contains('menu-open')) return;
        removeEventListener('scroll', onScroll);
        show();
      };
      addEventListener('scroll', onScroll, { passive: true });
    }
  }
})();
